import { DUNGEON_STATUSES } from './data/dungeon';

/**
 * Status effects: timed, stackable conditions on an entity (slows, qinggong seals, self-buffs).
 * Pure data plus small helpers; the World applies and ticks them.
 */
export interface StatusDef {
  readonly id: string;
  readonly name: string;
  /** Han glyph for the status icon (must be in UI_GLYPHS). */
  readonly glyph: string;
  readonly kind: 'buff' | 'debuff';
  readonly maxStacks: number;
  /** Seconds; refreshed whenever a stack is added. */
  readonly duration: number;
  /** Movement speed multiplier per stack (0.9 = 10% slower per stack). */
  readonly speedMul?: number;
  /** Bearer cannot jump or dash. */
  readonly noQinggong?: boolean;
  /** Reaching max stacks stuns the bearer for this long and clears the status. */
  readonly stunAtMax?: number;
  /** Jumping or dashing while marked stuns the bearer for this long and clears the status. */
  readonly trapStun?: number;
  /** Damage over time: fraction of max HP lost per second, per stack. */
  readonly dotPct?: number;
  /** Multiplies damage the bearer takes / deals. */
  readonly damageTakenMul?: number;
  readonly damageDealtMul?: number;
  /** Added to the bearer's crit chance. */
  readonly critAdd?: number;
  /** Bearer cannot use moves (acupoint seal). */
  readonly noSkills?: boolean;
  /** Fraction of damage the bearer deals returned to them as HP. */
  readonly lifesteal?: number;
  /** A lasting gift of a neigong (kept while that neigong is active and trained far enough). */
  readonly passive?: boolean;
  /** Held on by a rule while its cause lasts (carrying a sack): cleansing moves leave it alone. */
  readonly kept?: boolean;
  /** Healing the bearer receives × this, per stack (poison 0.9: five stacks heal at 0.59). */
  readonly healMul?: number;
  /** Extra damage over time: fraction of max HP per metre moved, per stack; ×2 in qinggong (bleed). */
  readonly moveDot?: number;
  /** The bearer cannot move, act or block: the World's stun timer shown as a status (systems/statusRules.ts). */
  readonly stun?: boolean;
  /** Ends on a single hit of at least this fraction of the bearer's max HP. */
  readonly breaksOnHeavy?: number;
  /** Which cleanse lifts it (docs/systems/SKILLS_V2.md 4.3): any cleansing move, only a strong one, or none. */
  readonly cleanse?: 'any' | 'strong' | 'none';
  /** From this many stacks on, only a strong cleanse lifts it (poison 3). */
  readonly strongFrom?: number;
  /** A stack added at max stacks bursts for this fraction of max HP (poison 0.03). */
  readonly burstAtMax?: number;
  /** The plain damage over time × this once the bearer has stood still for a second (bleed 0.5). */
  readonly stillMul?: number;
  /**
   * A delayed wound (แผลเปิดช้า, one-cut): when the status runs out on its own timer (not a cleanse or a cash-out), the
   * bearer loses this fraction of max HP per stack (systems/lateWound.ts; never the killing blow). Each new stack restarts it.
   */
  readonly woundPct?: number;
  /** PvP diminishing-returns group. */
  readonly drCategory?: 'stun' | 'slow' | 'silence';
  /** The bearer's loop motion and overhead FX id (the client reads it). */
  readonly anim?: string;
  readonly desc: string;
}

const list: StatusDef[] = [
  {
    id: 'snow-daze',
    name: 'เหน็บหนาว',
    glyph: '凝',
    kind: 'debuff',
    maxStacks: 3,
    duration: 6,
    speedMul: 0.88,
    stunAtMax: 1,
    desc: 'ช้าลงทีละชั้น ครบ 3 ชั้นจะมึน 1 วินาที',
  },
  {
    // ดาบชักเร็วโคมเหล็ก (kit audit 2026-10-13 piece 5): the quick-draw's lightning, no slow (snow-daze slows; this one
    // only counts to the stun) and the stacks sp-verdict cashes out
    id: 'thunder-daze',
    name: 'อัสนีชา',
    glyph: '雷',
    kind: 'debuff',
    maxStacks: 3,
    duration: 5,
    stunAtMax: 0.8,
    cleanse: 'any',
    desc: 'สายฟ้าแล่นตามคมดาบ ชาไปทั้งตัวทีละชั้น ครบ 3 ชั้นจะมึนงง 0.8 วินาที',
  },
  {
    // ขีดเดียวไร้ถ้อย (kit audit 2026-10-13 piece 11): the cut is drawn now, the wound opens when the blade stops
    id: 'late-wound',
    name: 'แผลเปิดช้า',
    glyph: '痕',
    kind: 'debuff',
    maxStacks: 3,
    duration: 1.5,
    woundPct: 0.02,
    cleanse: 'any',
    desc: 'รอยกระบี่ที่ยังไม่เปิด ครบ 1.5 วินาทีแผลจะเปิด เสียพลังชีวิต 2% ต่อชั้น ถูกขีดซ้ำเวลาจะเริ่มนับใหม่',
  },
  {
    // ขีดเดียวไร้ถ้อย: stillness (sg-insight, and sg-screen's guard buff): slower feet, a heavier cut
    id: 'still-mind',
    name: 'จิตนิ่งไร้ถ้อย',
    glyph: '靜',
    kind: 'buff',
    maxStacks: 1,
    duration: 6,
    speedMul: 0.8,
    damageDealtMul: 1.2,
    desc: 'จิตนิ่งดุจหน้ากระดาษ เคลื่อนที่ช้าลง 20% แต่โจมตีแรงขึ้น 20%',
  },
  {
    id: 'chill',
    name: 'ลมหนาวเข้าแทรก',
    glyph: '寒',
    kind: 'debuff',
    maxStacks: 1,
    duration: 3,
    speedMul: 0.6,
    noQinggong: true,
    desc: 'ช้าลงมากและใช้วิชาตัวเบาไม่ได้',
  },
  {
    id: 'frost-trap',
    name: 'ผนึกน้ำแข็ง',
    glyph: '封',
    kind: 'debuff',
    maxStacks: 1,
    duration: 3,
    trapStun: 1,
    desc: 'ถ้าใช้วิชาตัวเบาจะมึน 1 วินาที',
  },
  {
    id: 'slow',
    name: 'ฝีเท้าหนัก',
    glyph: '緩',
    kind: 'debuff',
    maxStacks: 1,
    duration: 2,
    speedMul: 0.7,
    cleanse: 'any',
    drCategory: 'slow',
    desc: 'ความเร็วในการเคลื่อนที่ลดลง',
  },
  {
    id: 'haste',
    name: 'ก้าวลม',
    glyph: '疾',
    kind: 'buff',
    maxStacks: 1,
    duration: 3,
    speedMul: 1.5,
    desc: 'เคลื่อนที่เร็วขึ้นมาก',
  },
  {
    id: 'tide-ward',
    name: 'พลองตั้งการ์ด',
    glyph: '禦',
    kind: 'buff',
    maxStacks: 1,
    duration: 8,
    desc: 'ตั้งพลองรอรับ ถ้ารับท่าได้จะได้คลื่นหนุน',
  },
  {
    id: 'vow-guard',
    name: 'ตั้งปณิธานรอรับ',
    glyph: '願',
    kind: 'buff',
    maxStacks: 1,
    duration: 8,
    desc: 'ตั้งรับรอ ถ้ารับท่าได้จะได้เกราะลมปราณและฟื้นพลังชีวิต',
  },
  {
    id: 'bell-guard',
    name: 'ระฆังรอรับ',
    glyph: '罩',
    kind: 'buff',
    maxStacks: 1,
    duration: 8,
    desc: 'ตั้งรับรอ ถ้ารับท่าได้ระฆังทองจะครอบกายและได้ความโกรธ',
  },
  {
    id: 'decree-guard',
    name: 'ป้ายโองการรอรับ',
    glyph: '令',
    kind: 'buff',
    maxStacks: 1,
    duration: 8,
    desc: 'ตั้งรับรอ ถ้ารับท่าได้จะโจมตีแรงขึ้น',
  },
  {
    id: 'loyal-guard',
    name: 'ใจภักดีรอรับ',
    glyph: '忠',
    kind: 'buff',
    maxStacks: 1,
    duration: 8,
    desc: 'ตั้งรับรอ ถ้ารับท่าได้จะเร็วขึ้นและได้ลมปราณคืน',
  },
  {
    id: 'mirror-guard',
    name: 'เงาจันทร์รอรับ',
    glyph: '鏡',
    kind: 'buff',
    maxStacks: 1,
    duration: 8,
    desc: 'ตั้งรับรอ ถ้ารับท่าได้จะติดดาบจันทร์',
  },
  {
    id: 'trance-guard',
    name: 'รำรอรับ',
    glyph: '舞',
    kind: 'buff',
    maxStacks: 1,
    duration: 8,
    desc: 'ตั้งรับรอ ถ้ารับท่าได้จะเข้าภวังค์',
  },
  {
    id: 'pulse-guard',
    name: 'นิ้วรอรับ',
    glyph: '指',
    kind: 'buff',
    maxStacks: 1,
    duration: 8,
    desc: 'ตั้งรับรอ ถ้าตั้งรับท่าได้จะก้าวลื่นไหลและได้ลมปราณคืน',
  },
  {
    id: 'screen-guard',
    name: 'ฉากหมึกรอรับ',
    glyph: '屏',
    kind: 'buff',
    maxStacks: 1,
    duration: 8,
    desc: 'ตั้งรับรอ ถ้าตั้งรับท่าได้จะจิตนิ่งไร้ถ้อย',
  },
  {
    id: 'fury-guard',
    name: 'เลือดพยัคฆ์รอรับ',
    glyph: '虎',
    kind: 'buff',
    maxStacks: 1,
    duration: 8,
    desc: 'ตั้งรับรอ ถ้าตั้งรับท่าได้จะคลั่งโลหิต',
  },
  {
    id: 'cliff-guard',
    name: 'ผาแดงรอรับ',
    glyph: '崖',
    kind: 'buff',
    maxStacks: 1,
    duration: 8,
    desc: 'ตั้งรับรอ ถ้าตั้งรับท่าได้จะยืนมั่นไม่ถอย',
  },
  {
    id: 'scale-guard',
    name: 'เกล็ดมังกรรอรับ',
    glyph: '鱗',
    kind: 'buff',
    maxStacks: 1,
    duration: 8,
    desc: 'ตั้งรับรอ ถ้าตั้งรับท่าได้จะเกล็ดมังกรขึ้นเต็มกาย',
  },
  {
    id: 'smoke-guard',
    name: 'ควันรอรับ',
    glyph: '煙',
    kind: 'buff',
    maxStacks: 1,
    duration: 8,
    desc: 'ตั้งรับรอ ถ้าตั้งรับท่าได้จะซ่อนกายในควัน',
  },
  {
    id: 'pulse-flow',
    name: 'ก้าวนิ้วลื่นไหล',
    glyph: '流',
    kind: 'buff',
    maxStacks: 1,
    duration: 5,
    speedMul: 1.25,
    desc: 'ก้าวลื่นไหลดุจน้ำ เคลื่อนที่เร็วขึ้น 25%',
  },
  {
    id: 'cliff-stand',
    name: 'ยืนมั่นไม่ถอย',
    glyph: '崖',
    kind: 'buff',
    maxStacks: 1,
    duration: 5,
    damageTakenMul: 0.75,
    damageDealtMul: 1.2,
    desc: 'ยืนมั่นดุจผา รับความเสียหายลดลง 25% โจมตีแรงขึ้น 20%',
  },
  {
    id: 'dragon-scale',
    name: 'เกล็ดมังกร',
    glyph: '鱗',
    kind: 'buff',
    maxStacks: 1,
    duration: 6,
    damageTakenMul: 0.7,
    lifesteal: 0.06,
    desc: 'เกล็ดมังกรขึ้นเต็มกาย รับความเสียหายลดลง 30% ดูดเลือด 6%',
  },
  {
    id: 'smoke-shadow',
    name: 'เงาในควัน',
    glyph: '影',
    kind: 'buff',
    maxStacks: 1,
    duration: 4,
    speedMul: 1.3,
    critAdd: 0.2,
    desc: 'ซ่อนกายในควัน เคลื่อนที่เร็วขึ้น 30% คริติคอลสูงขึ้น 20%',
  },
  {
    id: 'nine-guard',
    name: 'คำสาบานรอรับ',
    glyph: '誓',
    kind: 'buff',
    maxStacks: 1,
    duration: 8,
    desc: 'ตั้งรับรอ ถ้าตั้งรับท่าได้จะได้ผลหนุนของวิชา',
  },
  {
    id: 'nine-resolve',
    name: 'ปณิธานเก้ากระบี่',
    glyph: '九',
    kind: 'buff',
    maxStacks: 1,
    duration: 5,
    damageDealtMul: 1.15,
    critAdd: 0.1,
    desc: 'โจมตีแรงขึ้น 15% คริติคอลสูงขึ้น 10%',
  },
  {
    id: 'cloud-guard',
    name: 'เมฆรอรับ',
    glyph: '雲',
    kind: 'buff',
    maxStacks: 1,
    duration: 8,
    desc: 'ตั้งรับรอ ถ้าตั้งรับท่าได้จะได้ผลหนุนของวิชา',
  },
  {
    id: 'cloud-step',
    name: 'ก้าวเมฆ',
    glyph: '雲',
    kind: 'buff',
    maxStacks: 1,
    duration: 4,
    speedMul: 1.35,
    desc: 'เคลื่อนที่เร็วขึ้น 35%',
  },
  {
    id: 'breeze-guard',
    name: 'หมอกลมรอรับ',
    glyph: '霧',
    kind: 'buff',
    maxStacks: 1,
    duration: 8,
    desc: 'ตั้งรับรอ ถ้าตั้งรับท่าได้จะได้ผลหนุนของวิชา',
  },
  {
    id: 'breeze-veil',
    name: 'ม่านหมอกลม',
    glyph: '霧',
    kind: 'buff',
    maxStacks: 1,
    duration: 5,
    damageTakenMul: 0.8,
    speedMul: 1.15,
    desc: 'รับความเสียหายลดลง 20% เคลื่อนที่เร็วขึ้น 15%',
  },
  {
    id: 'yield-guard',
    name: 'อ่อนรอรับ',
    glyph: '柔',
    kind: 'buff',
    maxStacks: 1,
    duration: 8,
    desc: 'ตั้งรับรอ ถ้าตั้งรับท่าได้จะได้ผลหนุนของวิชา',
  },
  {
    id: 'taiji-yield',
    name: 'อ่อนหยุ่นไทชิ',
    glyph: '柔',
    kind: 'buff',
    maxStacks: 1,
    duration: 4,
    damageTakenMul: 0.6,
    desc: 'รับความเสียหายลดลง 40%',
  },
  {
    id: 'focus-guard',
    name: 'จิตนิ่งรอรับ',
    glyph: '寧',
    kind: 'buff',
    maxStacks: 1,
    duration: 8,
    desc: 'ตั้งรับรอ ถ้าตั้งรับท่าได้จะได้ผลหนุนของวิชา',
  },
  {
    id: 'frost-guard',
    name: 'หิมะรอรับ',
    glyph: '雪',
    kind: 'buff',
    maxStacks: 1,
    duration: 8,
    desc: 'ตั้งรับรอ ถ้าตั้งรับท่าได้จะได้ผลหนุนของวิชา',
  },
  {
    id: 'arhat-guard',
    name: 'ระฆังอรหันต์รอรับ',
    glyph: '鐘',
    kind: 'buff',
    maxStacks: 1,
    duration: 8,
    desc: 'ตั้งรับรอ ถ้าตั้งรับท่าได้จะได้ผลหนุนของวิชา',
  },
  {
    id: 'arhat-bell',
    name: 'ระฆังอรหันต์',
    glyph: '羅',
    kind: 'buff',
    maxStacks: 1,
    duration: 4,
    damageTakenMul: 0.6,
    damageDealtMul: 1.1,
    desc: 'รับความเสียหายลดลง 40% โจมตีแรงขึ้น 10%',
  },
  {
    id: 'brocade-guard',
    name: 'สัตย์รอรับ',
    glyph: '諾',
    kind: 'buff',
    maxStacks: 1,
    duration: 8,
    desc: 'ตั้งรับรอ ถ้าตั้งรับท่าได้จะได้ผลหนุนของวิชา',
  },
  {
    id: 'brocade-oath',
    name: 'สัตย์ดาบแพรทอง',
    glyph: '諾',
    kind: 'buff',
    maxStacks: 1,
    duration: 5,
    damageDealtMul: 1.2,
    critAdd: 0.1,
    desc: 'โจมตีแรงขึ้น 20% คริติคอลสูงขึ้น 10%',
  },
  {
    id: 'bliss-guard',
    name: 'ก้าวสุขรอรับ',
    glyph: '樂',
    kind: 'buff',
    maxStacks: 1,
    duration: 8,
    desc: 'ตั้งรับรอ ถ้าตั้งรับท่าได้จะได้ผลหนุนของวิชา',
  },
  {
    id: 'bliss-step',
    name: 'ก้าวสราญ',
    glyph: '樂',
    kind: 'buff',
    maxStacks: 1,
    duration: 5,
    speedMul: 1.2,
    lifesteal: 0.05,
    desc: 'เคลื่อนที่เร็วขึ้น 20% ดูดเลือด 5%',
  },
  {
    id: 'brush-guard',
    name: 'พู่กันรอรับ',
    glyph: '筆',
    kind: 'buff',
    maxStacks: 1,
    duration: 8,
    desc: 'ตั้งรับรอ ถ้าตั้งรับท่าได้จะได้ผลหนุนของวิชา',
  },
  {
    id: 'brush-ink',
    name: 'ปราณหมึกกำบัง',
    glyph: '墨',
    kind: 'buff',
    maxStacks: 1,
    duration: 5,
    damageTakenMul: 0.8,
    critAdd: 0.15,
    desc: 'รับความเสียหายลดลง 20% คริติคอลสูงขึ้น 15%',
  },
  {
    id: 'sabre-guard',
    name: 'ดาบชาดรอรับ',
    glyph: '刀',
    kind: 'buff',
    maxStacks: 1,
    duration: 8,
    desc: 'ตั้งรับรอ ถ้าตั้งรับท่าได้จะได้ผลหนุนของวิชา',
  },
  {
    id: 'sabre-rage',
    name: 'โลหิตดาบคลั่ง',
    glyph: '怒',
    kind: 'buff',
    maxStacks: 1,
    duration: 5,
    damageDealtMul: 1.25,
    lifesteal: 0.08,
    desc: 'โจมตีแรงขึ้น 25% ดูดเลือด 8%',
  },
  {
    id: 'golden-guard',
    name: 'มังกรทองรอรับ',
    glyph: '金',
    kind: 'buff',
    maxStacks: 1,
    duration: 8,
    desc: 'ตั้งรับรอ ถ้าตั้งรับท่าได้จะได้ผลหนุนของวิชา',
  },
  {
    id: 'golden-roar',
    name: 'คำรามมังกรทอง',
    glyph: '吼',
    kind: 'buff',
    maxStacks: 1,
    duration: 4,
    damageDealtMul: 1.3,
    desc: 'โจมตีแรงขึ้น 30%',
  },
  {
    id: 'moon-edge',
    name: 'ดาบจันทร์',
    glyph: '蝕',
    kind: 'buff',
    maxStacks: 1,
    duration: 5,
    critAdd: 0.25,
    desc: 'คมดาบจันทร์ ติดคริติคอลง่ายขึ้น',
  },
  {
    id: 'tide-surge',
    name: 'คลื่นหนุน',
    glyph: '浪',
    kind: 'buff',
    maxStacks: 1,
    duration: 6,
    desc: 'คลื่นหนุนพลอง ท่ากวาดคลื่นครั้งถัดไปแรงขึ้น',
  },
  {
    id: 'frost-veil',
    name: 'ก้าวหิมะคลุมกาย',
    glyph: '疾',
    kind: 'buff',
    maxStacks: 1,
    duration: 4,
    speedMul: 1.5,
    damageTakenMul: 0.6,
    desc: 'เคลื่อนที่เร็วขึ้นมาก และรับความเสียหายน้อยลง',
  },
  {
    id: 'snow-pride',
    name: 'ใจเหมันต์',
    glyph: '凜',
    kind: 'buff',
    maxStacks: 3,
    duration: 12,
    desc: 'กระบวนท่าถัดไปของกระบี่ธารเยือกแข็งร่ายเป็นร่างเต็มกำลัง แรงขึ้น (ใช้ทีละชั้น)',
  },
  {
    id: 'iron-bell',
    name: 'ระฆังทองคุ้มกาย',
    glyph: '鐘',
    kind: 'buff',
    maxStacks: 1,
    duration: 5,
    damageTakenMul: 0.55,
    desc: 'กายดุจระฆังทอง รับความเสียหายลดลงมาก',
  },
  {
    id: 'bleed',
    name: 'เลือดไหล',
    glyph: '血',
    kind: 'debuff',
    maxStacks: 3,
    duration: 5,
    dotPct: 0.012,
    moveDot: 0.004,
    stillMul: 0.5,
    cleanse: 'any',
    desc: 'เสียเลือดต่อเนื่องทุกวินาที ยิ่งเคลื่อนไหวยิ่งไหลมาก (วิชาตัวเบาสองเท่า) ทบซ้อนได้ 3 ชั้น',
  },
  {
    id: 'war-cry',
    name: 'สาบานต่อบัลลังก์',
    glyph: '誓',
    kind: 'buff',
    maxStacks: 1,
    duration: 6,
    damageDealtMul: 1.25,
    desc: 'สร้างความเสียหายรุนแรงขึ้น',
  },
  {
    id: 'poison',
    name: 'พิษ',
    glyph: '毒',
    kind: 'debuff',
    maxStacks: 5,
    duration: 6,
    dotPct: 0.008,
    healMul: 0.9,
    cleanse: 'any',
    strongFrom: 3,
    burstAtMax: 0.03,
    desc: 'พิษกัดกินพลังชีวิตและลดการฟื้นฟูพลังชีวิต ทบซ้อนได้ 5 ชั้น',
  },
  {
    id: 'stun',
    name: 'มึนงง',
    glyph: '暈',
    kind: 'debuff',
    maxStacks: 1,
    duration: 1,
    stun: true,
    breaksOnHeavy: 0.12,
    cleanse: 'any',
    drCategory: 'stun',
    anim: 'dizzy',
    desc: 'ขยับ ใช้กระบวนท่า และตั้งรับไม่ได้ โดนโจมตีหนักจะหายมึน หลังหายจะไม่มึนซ้ำ 2 วินาที',
  },
  {
    id: 'charm',
    name: 'มนตร์จันทร์มัว',
    glyph: '媚',
    kind: 'debuff',
    maxStacks: 1,
    duration: 4,
    speedMul: 0.85,
    damageDealtMul: 0.75,
    desc: 'ถูกมนตร์สะกด โจมตีเบาลงและเคลื่อนที่ช้า',
  },
  {
    id: 'silence',
    name: 'ถูกปิดจุด',
    glyph: '閉',
    kind: 'debuff',
    maxStacks: 1,
    duration: 2,
    noSkills: true,
    cleanse: 'strong',
    drCategory: 'silence',
    desc: 'ชีพจรถูกปิด ใช้กระบวนท่าไม่ได้',
  },
  {
    id: 'ward',
    name: 'พัดลมบังกาย',
    glyph: '扇',
    kind: 'buff',
    maxStacks: 1,
    duration: 5,
    damageTakenMul: 0.7,
    desc: 'ลมพัดปัดป้อง รับความเสียหายลดลง',
  },
  {
    id: 'focus',
    name: 'สมาธิหมึกนิ่ง',
    glyph: '悟',
    kind: 'buff',
    maxStacks: 1,
    duration: 6,
    critAdd: 0.25,
    desc: 'จิตนิ่งแน่วแน่ โอกาสคริติคอลสูงขึ้นมาก',
  },
  {
    id: 'focus-calm',
    name: 'สมาธิกระบี่นิ่ง',
    glyph: '寧',
    kind: 'buff',
    maxStacks: 1,
    duration: 6,
    critAdd: 0.15,
    desc: 'จิตนิ่งแน่วแน่ โอกาสคริติคอลสูงขึ้น',
  },
  {
    id: 'blood-frenzy',
    name: 'คลั่งโลหิต',
    glyph: '狂',
    kind: 'buff',
    maxStacks: 1,
    duration: 6,
    damageDealtMul: 1.3,
    damageTakenMul: 1.1,
    lifesteal: 0.1,
    desc: 'คลั่งเลือด โจมตีแรงขึ้น 30% ดูดเลือด 10% แต่รับความเสียหายมากขึ้น 10%',
  },
  // Meals cooked by the trades (data/professions.ts); one lasts ten minutes.
  {
    id: 'meal-might',
    name: 'อิ่มท้องมีแรง',
    glyph: '食',
    kind: 'buff',
    maxStacks: 1,
    duration: 600,
    damageDealtMul: 1.08,
    desc: 'กินอิ่ม โจมตีแรงขึ้น 8%',
  },
  {
    id: 'meal-guard',
    name: 'อุ่นกายใจ',
    glyph: '膳',
    kind: 'buff',
    maxStacks: 1,
    duration: 600,
    damageTakenMul: 0.92,
    desc: 'แกงร้อนอุ่นกาย รับความเสียหายลดลง 8%',
  },
  {
    id: 'meal-feast',
    name: 'งานเลี้ยงย่อม',
    glyph: '宴',
    kind: 'buff',
    maxStacks: 1,
    duration: 600,
    damageDealtMul: 1.1,
    damageTakenMul: 0.94,
    critAdd: 0.03,
    desc: 'อิ่มเอมงานเลี้ยง โจมตีแรงขึ้น 10% รับความเสียหายลดลง 6% คริติคอล +3%',
  },
  {
    // หมั่นโถว (the rice → mantou chain): a short snack, three minutes.
    id: 'meal-mantou',
    name: 'อิ่มหมั่นโถว',
    glyph: '饅',
    kind: 'buff',
    maxStacks: 1,
    duration: 180,
    speedMul: 1.05,
    damageTakenMul: 0.95,
    desc: 'หมั่นโถวอุ่นท้อง วิ่งเร็วขึ้น 5% รับความเสียหายลดลง 5%',
  },
  {
    id: 'sunder',
    name: 'เกราะร้าว',
    glyph: '甲',
    kind: 'debuff',
    maxStacks: 1,
    duration: 5,
    damageTakenMul: 1.15,
    desc: 'เกราะกายร้าว รับความเสียหายเพิ่มขึ้น 15%',
  },
  {
    id: 'shackle',
    name: 'ถูกตรวน',
    glyph: '枷',
    kind: 'debuff',
    maxStacks: 1,
    duration: 3,
    speedMul: 0.6,
    noQinggong: true,
    cleanse: 'any',
    drCategory: 'slow',
    desc: 'ถูกตรึงด้วยโซ่หรือจุดชีพจร ช้าลงมากและใช้วิชาตัวเบาไม่ได้',
  },
  {
    id: 'trance',
    name: 'ภวังค์ระบำมาร',
    glyph: '舞',
    kind: 'buff',
    maxStacks: 1,
    duration: 6,
    damageDealtMul: 1.15,
    speedMul: 1.15,
    desc: 'ร่ายรำจนเข้าภวังค์ โจมตีแรงขึ้น 15% เคลื่อนที่เร็วขึ้น 15%',
  },
  {
    // Carrying a sack (systems/offlineWork.ts keeps it on while the sack is on the shoulder, data/offlineWork.ts `KIDNAP`).
    id: 'sack-carry',
    name: 'แบกกระสอบ',
    glyph: '囊',
    kind: 'debuff',
    maxStacks: 1,
    duration: 1,
    speedMul: 0.6,
    noQinggong: true,
    kept: true,
    desc: 'แบกกระสอบอยู่ เดินช้าลง วิ่งวิชาตัวเบา กระโดด และขี่พาหนะไม่ได้ ถูกตีจะทำกระสอบหล่น',
  },
  // Neigong gifts at level 6: never run out while that neigong is active.
  ...(
    [
      [
        'np-blood',
        'โลหิตเดือดพล่าน',
        '沸',
        { damageDealtMul: 1.1, critAdd: 0.05 },
        'โจมตีแรงขึ้น 10% คริติคอล +5%',
      ],
      [
        'np-cloud',
        'เมฆาลอยล่อง',
        '雲',
        { speedMul: 1.1, damageTakenMul: 0.95 },
        'เคลื่อนที่เร็วขึ้น 10% รับความเสียหายลดลง 5%',
      ],
      ['np-mist-breath', 'หมอกคุ้มกาย', '儀', { damageTakenMul: 0.88 }, 'รับความเสียหายลดลง 12%'],
      ['np-vajra', 'กายระฆัง', '剛', { damageTakenMul: 0.85 }, 'รับความเสียหายลดลง 15%'],
      [
        'np-guard',
        'ประกายโคมเหล็ก',
        '衛',
        { damageDealtMul: 1.08, critAdd: 0.04 },
        'โจมตีแรงขึ้น 8% คริติคอล +4%',
      ],
      [
        'np-bliss',
        'เงาไว้อาลัยจันทร์มัว',
        '魅',
        { critAdd: 0.08, speedMul: 1.05 },
        'คริติคอล +8% เคลื่อนที่เร็วขึ้น 5%',
      ],
      ['np-scholar', 'จิตใสหมึกสน', '澄', { critAdd: 0.1 }, 'คริติคอล +10%'],
      ['np-bloodthirst', 'กระหายโลหิต', '渴', { lifesteal: 0.08 }, 'ดูดเลือด 8% ของความเสียหายที่ทำ'],
      // Tier-2 scriptures: the tier-1 gift, grown.
      [
        'np-nine',
        'เก้ากระบี่ในใจ',
        '心',
        { damageDealtMul: 1.12, critAdd: 0.06 },
        'โจมตีแรงขึ้น 12% คริติคอล +6%',
      ],
      [
        'np-void',
        'เมฆาว่างเปล่า',
        '虛',
        { speedMul: 1.12, damageTakenMul: 0.92 },
        'เคลื่อนที่เร็วขึ้น 12% รับความเสียหายลดลง 8%',
      ],
      [
        'np-mist-wheel',
        'แปดทิศลมหมอก',
        '卦',
        { damageTakenMul: 0.84, critAdd: 0.03 },
        'รับความเสียหายลดลง 16% คริติคอล +3%',
      ],
      [
        'np-bell-layers',
        'ระฆังพันชั้น',
        '髓',
        { damageTakenMul: 0.82, damageDealtMul: 1.03 },
        'รับความเสียหายลดลง 18% โจมตีแรงขึ้น 3%',
      ],
      [
        'np-fish',
        'ประกายโคมพิทักษ์',
        '魚',
        { damageDealtMul: 1.1, critAdd: 0.06 },
        'โจมตีแรงขึ้น 10% คริติคอล +6%',
      ],
      [
        'np-demon',
        'จิตเงาจันทร์',
        '妖',
        { critAdd: 0.1, speedMul: 1.06, lifesteal: 0.03 },
        'คริติคอล +10% เคลื่อนที่เร็วขึ้น 6% ดูดเลือด 3%',
      ],
      ['np-sage', 'จิตปราชญ์', '哲', { critAdd: 0.13 }, 'คริติคอล +13%'],
      [
        'np-bloodsea',
        'ทะเลโลหิต',
        '溟',
        { lifesteal: 0.1, damageDealtMul: 1.04 },
        'ดูดเลือด 10% ของความเสียหายที่ทำ โจมตีแรงขึ้น 4%',
      ],
      ['np-tide', 'คลื่นสามจังหวะ', '濤', { speedMul: 1.05, damageTakenMul: 0.96 }, 'เคลื่อนที่เร็วขึ้น 5% รับความเสียหายลดลง 4%'],
      ['np-tide2', 'คลื่นหนุนไผ่', '濤', { speedMul: 1.08, damageTakenMul: 0.94 }, 'เคลื่อนที่เร็วขึ้น 8% รับความเสียหายลดลง 6%'],
      ['np-tide3', 'รากไผ่ใต้คลื่น', '濤', { speedMul: 1.1, damageTakenMul: 0.92 }, 'เคลื่อนที่เร็วขึ้น 10% รับความเสียหายลดลง 8%'],
      ['np-snow', 'น้ำค้างแข็ง', '雪', { critAdd: 0.06 }, 'คริติคอล +6%'],
      ['np-snow2', 'สนยืนต้น', '雪', { critAdd: 0.09, damageTakenMul: 0.96 }, 'คริติคอล +9% รับความเสียหายลดลง 4%'],
      ['np-snow3', 'ธารเยือกใต้ราก', '雪', { critAdd: 0.12, damageTakenMul: 0.93 }, 'คริติคอล +12% รับความเสียหายลดลง 7%'],
      ['np-mist3', 'ม่านหมอกเหนือยอด', '儀', { damageTakenMul: 0.84 }, 'รับความเสียหายลดลง 16%'],
      ['np-crimson3', 'ฟ้าชาดสิ้นแสง', '溟', { lifesteal: 0.12, damageDealtMul: 1.06 }, 'ดูดเลือด 12% โจมตีแรงขึ้น 6%'],
      ['np-ink3', 'หมึกไร้ขอบ', '哲', { critAdd: 0.16 }, 'คริติคอล +16%'],
      ['np-bell3', 'ระฆังเงียบก้อง', '剛', { damageTakenMul: 0.8 }, 'รับความเสียหายลดลง 20%'],
      ['np-lantern3', 'โคมเหล็กส่องคืน', '衛', { damageDealtMul: 1.12, critAdd: 0.07 }, 'โจมตีแรงขึ้น 12% คริติคอล +7%'],
      ['np-moonveil3', 'ม่านจันทร์ลวง', '妖', { critAdd: 0.12, speedMul: 1.08, lifesteal: 0.04 }, 'คริติคอล +12% เคลื่อนที่เร็วขึ้น 8% ดูดเลือด 4%'],
    ] as const
  ).map(([id, name, glyph, fx, desc]): StatusDef => ({
    id,
    name,
    glyph,
    kind: 'buff',
    maxStacks: 1,
    duration: Infinity,
    passive: true,
    ...fx,
    desc: `ติดตัวจากกำลังภายในขั้น 6: ${desc}`,
  })),
  // ถ้ำคลื่นคำราม (data/dungeon.ts): sleep powder, the chain's root, the boss's iron skin.
  ...DUNGEON_STATUSES,
];

export const STATUSES: Readonly<Record<string, StatusDef>> = Object.fromEntries(list.map((s) => [s.id, s]));

export function getStatus(id: string): StatusDef {
  const s = STATUSES[id];
  if (!s) throw new Error(`Unknown status: ${id}`);
  return s;
}

/** A status on an entity. */
export interface StatusState {
  id: string;
  stacks: number;
  /** Seconds left. */
  t: number;
  /** Who applied it (credited if damage over time defeats the bearer). */
  srcId?: number;
}

export const stacksOf = (list: readonly StatusState[], id: string): number =>
  list.find((s) => s.id === id)?.stacks ?? 0;

/** Combined movement multiplier from every status. */
export function statusSpeed(list: readonly StatusState[]): number {
  let m = 1;
  for (const s of list) {
    const d = STATUSES[s.id];
    if (d?.speedMul) m *= Math.pow(d.speedMul, s.stacks);
  }
  return m;
}

export const sealedQinggong = (list: readonly StatusState[]): boolean =>
  list.some((s) => STATUSES[s.id]?.noQinggong);

/** Statuses that limit movement, removed by cleansing moves. */
export const isMovementDebuff = (id: string): boolean => {
  const d = STATUSES[id];
  return !!d && d.kind === 'debuff' && !d.kept && (!!d.speedMul || !!d.noQinggong || !!d.trapStun);
};

/**
 * Does an ordinary cleansing move (`cleanse: true`) lift this status? Its `cleanse` field says (`any`, below
 * `strongFrom` stacks); without one, the movement debuffs as before. `kept` never.
 */
export function cleansable(st: StatusState): boolean {
  const d = STATUSES[st.id];
  if (!d || d.kept || d.kind !== 'debuff') return false;
  if (d.cleanse) return d.cleanse === 'any' && !(d.strongFrom && st.stacks >= d.strongFrom);
  return isMovementDebuff(st.id);
}

export const STATUS_GLYPHS = list.map((s) => s.glyph).join('');

/** Product of a multiplier field over every status (e.g. damage taken). */
export function statusMul(list: readonly StatusState[], key: 'damageTakenMul' | 'damageDealtMul'): number {
  let m = 1;
  for (const s of list) m *= STATUSES[s.id]?.[key] ?? 1;
  return m;
}

export const statusCrit = (list: readonly StatusState[]): number =>
  list.reduce((a, s) => a + (STATUSES[s.id]?.critAdd ?? 0), 0);

/** Total lifesteal from every status (fraction of damage dealt healed back). */
export const statusLifesteal = (list: readonly StatusState[]): number =>
  list.reduce((a, s) => a + (STATUSES[s.id]?.lifesteal ?? 0), 0);

export const silenced = (list: readonly StatusState[]): boolean => list.some((s) => STATUSES[s.id]?.noSkills);

/** Healing received × every status's `healMul` (per stack). */
export function statusHealMul(list: readonly StatusState[]): number {
  let m = 1;
  for (const s of list) {
    const h = STATUSES[s.id]?.healMul;
    if (h) m *= h ** s.stacks;
  }
  return m;
}
